<script>
  import MediaQuery from "svelte-media-query";
  import Skeleton from "svelte-skeleton/dist/Skeleton.svelte";
  import ArrowLink from "../Common/ArrowLink.svelte";
  import Report from "./Report.svelte";
  import reportsFetch from "../../helpers/reportsFetch";
  import SpotlightReport from "../FeaturedReports/SpotlightReport.svelte";

  // State
  let featuredReports = reportsFetch("featured");
  let spotlightReport = reportsFetch("spotlight");
  let mobileSet = Promise.all([spotlightReport, featuredReports]);

  // Side-by-side vs stacked
  $: stacked = window.matchMedia("(max-width: 1080px)").matches;
  window.addEventListener("resize", () => {
    stacked = window.matchMedia("(max-width: 1080px)").matches;
  });

  // skeleton width helper
  const pctWidth = (n) => `width: calc(${n}% - 234px);`;
</script>

<MediaQuery query="(min-width: 581px)" let:matches>
  {#if matches}
    <div class="container">
      {#await spotlightReport}
        <div class="spotlight-skeleton-wrap">
          <Skeleton
            primaryColor="#E0E0E0"
            secondaryColor="#ebecef"
            height="184"
            width="391"
          >
            <rect width="40%" height="24" x="0" y="0" rx="5" ry="5" />
            <rect width="100%" height="24" x="0" y="40" rx="5" ry="5" />
            <rect width="80%" height="24" x="0" y="80" rx="5" ry="5" />
            <rect width="35%" height="24" x="0" y="120" rx="5" ry="5" />
            <rect width="60%" height="24" x="0" y="160" rx="5" ry="5" />
          </Skeleton>
        </div>
      {:then spotlightReport}
        <SpotlightReport report={spotlightReport[0]} />
      {/await}
      <div
        class="featured-reports"
        style={!location.pathname.includes("reports") && !stacked
          ? "padding-top: 0;"
          : ""}
      >
        {#await featuredReports}
          <Skeleton height="440" width="100%">
            <rect width="186" height="120" x="0" y="0" rx="12" ry="12" />
            <rect
              style={pctWidth(60)}
              height="24"
              x="234"
              y="8"
              rx="5"
              ry="5"
            />
            <rect
              style={pctWidth(100)}
              height="24"
              x="234"
              y="42"
              rx="5"
              ry="5"
            />
            <rect
              style={pctWidth(80)}
              height="24"
              x="234"
              y="76"
              rx="5"
              ry="5"
            />

            <rect width="186" height="120" x="0" y="160" rx="12" ry="12" />
            <rect
              style={pctWidth(60)}
              height="24"
              x="234"
              y="168"
              rx="5"
              ry="5"
            />
            <rect
              style={pctWidth(100)}
              height="24"
              x="234"
              y="202"
              rx="5"
              ry="5"
            />
            <rect
              style={pctWidth(80)}
              height="24"
              x="234"
              y="236"
              rx="5"
              ry="5"
            />

            <rect width="186" height="120" x="0" y="320" rx="12" ry="12" />
            <rect
              style={pctWidth(60)}
              height="24"
              x="234"
              y="328"
              rx="5"
              ry="5"
            />
            <rect
              style={pctWidth(100)}
              height="24"
              x="234"
              y="362"
              rx="5"
              ry="5"
            />
            <rect
              style={pctWidth(80)}
              height="24"
              x="234"
              y="396"
              rx="5"
              ry="5"
            />
          </Skeleton>
        {:then featuredReports}
          {#each featuredReports as featuredReport}
            <Report report={featuredReport} />
          {/each}

          <ArrowLink text="All Reports" href={"/reports/search?sort=date"} />
        {/await}
      </div>
    </div>
  {:else}
    <div
      class="container"
      style={location.pathname.includes("reports")
        ? "border-bottom: 2px solid #ebe9e9"
        : ""}
    >
      <div
        class="featured-reports"
        style={!location.pathname.includes("reports") && !stacked
          ? "padding-top: 0;"
          : ""}
      >
        {#await mobileSet}
          <h2>Featured Reports</h2>
          <Skeleton
            primaryColor="#E0E0E0"
            secondaryColor="#ebecef"
            height="424"
            width="100%"
          >
            <rect width="40%" height="24" x="0" y="0" rx="5" ry="5" />
            <rect width="100%" height="24" x="0" y="40" rx="5" ry="5" />
            <rect width="80%" height="24" x="0" y="80" rx="5" ry="5" />

            <rect width="35%" height="24" x="0" y="160" rx="5" ry="5" />
            <rect width="90%" height="24" x="0" y="200" rx="5" ry="5" />
            <rect width="60%" height="24" x="0" y="240" rx="5" ry="5" />

            <rect width="40%" height="24" x="0" y="320" rx="5" ry="5" />
            <rect width="100%" height="24" x="0" y="360" rx="5" ry="5" />
            <rect width="80%" height="24" x="0" y="400" rx="5" ry="5" />
          </Skeleton>
        {:then mobileSet}
          <h2>Featured Reports</h2>
          <Report report={mobileSet[0][0]} />
          {#each mobileSet[1] as featuredReport}
            <Report report={featuredReport} />
          {/each}
          <ArrowLink text="All Reports" href={"/reports/search"} />
        {/await}
      </div>
    </div>
  {/if}
</MediaQuery>

<style>
  .container {
    display: flex;
    align-items: stretch;
  }

  .spotlight-skeleton-wrap {
    flex-basis: 57%;
    position: relative;
    display: flex;
    align-items: center;
  }
  .spotlight-skeleton-wrap::after {
    content: "";
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 100vw;
    z-index: -1;
    background: #ebecef;
  }

  .featured-reports {
    flex-basis: 43%;
    padding: 44px 0 44px 48px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  @media screen and (max-width: 1416px) {
    .spotlight-skeleton-wrap {
      flex-basis: 50%;
    }
    .featured-reports {
      flex-basis: 50%;
    }
  }
  @media screen and (max-width: 1080px) {
    .container {
      flex-direction: column;
    }
    .spotlight-skeleton-wrap {
      padding: 40px 70px;
      width: 100vw;
      position: relative;
      left: -30px;
    }
    .featured-reports {
      padding: 40px 10px;
    }
  }
  @media screen and (max-width: 580px) {
    .featured-reports {
      align-items: flex-start;
    }
    .featured-reports :global(h2) {
      margin-bottom: 40px;
    }
  }
</style>
